import React, { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import {
  FaBan,
  FaBoxes,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileUpload,
  FaPaperPlane,
  FaTimesCircle,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  fetchPaged,
  FilterOption,
  fullName,
  materialFilters,
  qty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  HolderChip,
  IconAction,
  ListCard,
  OffCanvas,
  OutlineButton,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  StatusBadge,
  StatusTabs,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Opening balances - the stock that exists on paper and has to be loaded in.
 *
 * Loaded as a reviewable BATCH, because two hundred loose movements are
 * impossible to check and impossible to undo. Rows that fail validation are
 * kept with their error and the text that was typed, so an import can be
 * corrected rather than re-done. Nothing touches stock until the batch is
 * approved.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Awaiting Approval" },
  { key: "approved", label: "Posted" },
  { key: "rejected,cancelled", label: "Closed" },
]

const MaterialOpeningBalancesPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const canCreate = hasPermission("MaterialOpeningBalance", "create")
  const canApprove = hasPermission("MaterialOpeningBalance", "approve")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/opening-balances", {
        page,
        limit,
        status: status || undefined,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load opening balance batches"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/materials/opening-balances/template", { responseType: "blob" })
      const url = URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.download = "material-opening-balance-template.xlsx"
      link.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to download the template"))
    }
  }

  const act = async (row: any, action: "submit" | "approve" | "reject" | "cancel") => {
    let body: Record<string, any> = {}

    if (action === "submit" && row.invalid_rows > 0) {
      toast.error(`${row.invalid_rows} row(s) still have errors. Fix or remove them first.`)
      return
    }

    if (action === "reject" || action === "cancel") {
      const entered = await prompt({
        title: action === "reject" ? "Reject batch" : "Cancel batch",
        label: "Reason",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel batch",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      body = { remarks: entered, reason: entered }
    }

    if (action === "approve") {
      const ok = await confirm({
        title: "Post opening balances",
        message: (
          <>
            This adds <strong>{row.valid_rows}</strong> row(s) of stock to the books as at{" "}
            <strong>{dateText(row.as_on_date)}</strong>.
          </>
        ),
        warning: "Posted opening stock cannot be un-posted. A mistake has to be corrected with an adjustment report.",
        confirmText: "Post to stock",
        cancelText: "Not yet",
        variant: "warning",
      })
      if (!ok) return
    }

    setBusyId(row.id)
    try {
      const res = await api.post(`/materials/opening-balances/${row.id}/${action}`, body)
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      toast.error(apiError(e, "The action could not be completed"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaBoxes className="w-5 h-5" />}
        title="Opening Balances"
        subtitle="Load the stock you already hold on paper - in a warehouse, with a staff member or at a site. Import it or type it; either way it is approved before it reaches the books."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            <OutlineButton onClick={downloadTemplate}>
              <FaDownload className="w-4 h-4" /> Template
            </OutlineButton>
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaFileUpload className="w-4 h-4" /> New Batch
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="Batch number, title, file..."
                />
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={7}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading opening balance batches..."
          emptyText="No opening balance batches found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaBoxes />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Batch</Th>
              <Th>As On</Th>
              <Th>Source</Th>
              <Th>Rows</Th>
              <Th>Created By</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="rows"
                    />
                    {row.status === "draft" && canCreate ? (
                      <IconAction title="Submit for approval" disabled={busyId === row.id} onClick={() => act(row, "submit")}>
                        <FaPaperPlane />
                      </IconAction>
                    ) : null}
                    {row.status === "submitted" && canApprove ? (
                      <>
                        <IconAction title="Post to stock" tone="green" disabled={busyId === row.id} onClick={() => act(row, "approve")}>
                          <FaCheckCircle />
                        </IconAction>
                        <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row, "reject")}>
                          <FaTimesCircle />
                        </IconAction>
                      </>
                    ) : null}
                    {!["approved", "cancelled"].includes(row.status) && canCreate ? (
                      <IconAction title="Cancel batch" tone="gray" disabled={busyId === row.id} onClick={() => act(row, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell>
                  <div className="font-medium">{row.batch_number}</div>
                  <div className="text-xs text-gray-500">{row.title || row.source_file_name || "-"}</div>
                </Cell>
                <Cell>{dateText(row.as_on_date)}</Cell>
                <Cell className="capitalize">{row.source}</Cell>
                <Cell>
                  <span className="text-green-700">{row.valid_rows} ok</span>
                  {row.invalid_rows > 0 ? <span className="text-red-700 ml-2">{row.invalid_rows} with errors</span> : null}
                </Cell>
                <Cell>{fullName(row.creator)}</Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={7}>
                  <BatchRows batchId={row.id} editable={row.status === "draft" && canCreate} onChanged={load} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <BatchForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The preview. Invalid rows come first and carry their error next to the text
 * that was actually typed - a row that says 'item "FOO-12" was not found' is
 * the difference between a fixable import and a mystery.
 */
const BatchRows: React.FC<{ batchId: number; editable: boolean; onChanged: () => void }> = ({
  batchId,
  editable,
  onChanged,
}) => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<any[]>([])
  const [onlyInvalid, setOnlyInvalid] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/materials/opening-balances/${batchId}`, {
        params: { limit: 100, only_invalid: onlyInvalid || undefined },
      })
      setRows(res.data?.data?.items || [])
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load the batch rows"))
    } finally {
      setLoading(false)
    }
  }, [batchId, onlyInvalid])

  useEffect(() => {
    load()
  }, [load])

  const removeRow = async (rowId: number) => {
    setBusy(rowId)
    try {
      await api.delete(`/materials/opening-balances/${batchId}/rows/${rowId}`)
      toast.success("Row removed")
      load()
      onChanged()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to remove the row"))
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <p className="text-sm text-gray-500 py-3">Loading rows...</p>

  return (
    <div className="space-y-2 py-2">
      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input type="checkbox" checked={onlyInvalid} onChange={(e) => setOnlyInvalid(e.target.checked)} />
        Show only rows with errors
      </label>
      <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
        <thead className="bg-gray-100">
          <tr>
            <Th>Row</Th>
            <Th>Item</Th>
            <Th>Holder</Th>
            <Th>Project</Th>
            <Th>Quantity</Th>
            <Th>Rate</Th>
            <Th>Status</Th>
            {editable ? <Th></Th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.id} className={row.is_valid ? "" : "bg-red-50"}>
              <Td>{row.row_number ?? "-"}</Td>
              <Td>{row.item?.item_name || row.raw_item_code || "-"}</Td>
              <Td>
                {row.holder_type ? (
                  <HolderChip type={row.holder_type} label={row.holder_label} code={row.holder_code} />
                ) : (
                  <span className="text-gray-500">{row.raw_holder_reference || "-"}</span>
                )}
              </Td>
              <Td>{row.project?.project_short_name || row.project?.project_name || row.raw_project || "-"}</Td>
              <Td>{qty(row.quantity)}</Td>
              <Td>{row.unit_rate ?? "-"}</Td>
              <Td>
                {row.is_valid ? (
                  <span className="text-green-700 text-xs">Ready</span>
                ) : (
                  <span className="text-red-700 text-xs flex items-start gap-1">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    {row.error_message}
                  </span>
                )}
              </Td>
              {editable ? (
                <Td>
                  <button
                    disabled={busy === row.id}
                    onClick={() => removeRow(row.id)}
                    className="text-xs text-red-600 hover:underline disabled:opacity-40"
                  >
                    Remove
                  </button>
                </Td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 ? <p className="text-sm text-gray-500">No rows to show</p> : null}
    </div>
  )
}

const BatchForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [project, setProject] = useState<FilterOption | null>(null)
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().slice(0, 10))
  const [title, setTitle] = useState("")
  const [remarks, setRemarks] = useState("")
  const [file, setFile] = useState<File | null>(null)

  const submit = async () => {
    if (!file) return toast.error("Choose the filled-in template to upload")

    const payload = new FormData()
    payload.append("file", file)
    payload.append("as_on_date", asOnDate)
    if (project) payload.append("project_id", String(project.id))
    if (title) payload.append("title", title)
    if (remarks) payload.append("remarks", remarks)

    setSaving(true)
    try {
      const res = await api.post("/materials/opening-balances", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      toast.success(res.data?.message || "Batch created")
      const errors = res.data?.data?.sample_errors || []
      if (errors.length) {
        toast(`${errors.length} row(s) need attention - open the batch to see why`, { icon: "⚠️" })
      }
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to create the batch"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <OffCanvas
      onClose={onClose}
      title="New opening balance batch"
      subtitle="Upload the filled-in template. Rows are validated on arrival and nothing reaches the books until the batch is approved."
      width="md"
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton loading={saving} onClick={submit}>
            Upload and validate
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="As on date" required hint="The date the paper stock was counted">
          <TextInput type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
        </Field>
        <Field label="Default project" hint="Used for rows that leave the Project column blank">
          <AsyncPicker
            value={project}
            onChange={setProject}
            fetcher={(s) => materialFilters.projects(s)}
            placeholder="Select project..."
            emptyHint="You are not assigned to any project"
          />
        </Field>
      </div>

      <Field label="Title">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Central warehouse count, March" />
      </Field>

      <Field
        label="Workbook"
        required
        hint="Holder Type must be warehouse, staff or site. Holder Reference is the warehouse code, employee code or site code to match."
      >
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-gray-100 file:text-gray-700"
        />
      </Field>

      <Field label="Remarks">
        <TextArea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </Field>
    </OffCanvas>
  )
}

export default MaterialOpeningBalancesPage
